/**
 * @mention primitives — shared by the tasks backend (which derives access grants
 * from mentions) and by tasks-ui (which renders them).
 *
 * They live in the SDK rather than in `@lognai/plugin-tasks` because tasks-ui
 * cannot import runtime values from that package (it resolves to a CJS server
 * bundle). Duplicating the regex in both packages is not an option: if the
 * parser and the renderer ever drift, the set of users granted access stops
 * matching the set of users shown on screen — a silent authorization bug.
 *
 * ## Storage format: `@[Priya Kumar](user:USER_ID)`
 *
 * The **user id is the authority; the label is only a display cache.** A bare
 * `@Priya Kumar` is not viable: display names are not unique, they contain
 * spaces (so there is no parse terminator), and they change. Deriving a *view
 * grant* by matching a name would mean a rename — or two colleagues sharing a
 * name — silently grants the wrong person access to the task.
 *
 * Consumers MUST treat the label as untrusted display text: the server reads
 * only the id group, and renderers emit React nodes (never innerHTML).
 */
/**
 * A label may not contain the marker's own delimiters or a newline, which is
 * what makes the format unambiguous and unforgeable — see `sanitizeMentionLabel`.
 * The id charset matches the platform's user ids (uuid / timestamp-ish strings).
 */
const MENTION_PATTERN = /@\[([^\]\r\n]{1,100})\]\(user:([A-Za-z0-9_-]{1,64})\)/g;
/** Upper bound on mentions in a single text, so grant lists stay bounded. */
export const MAX_MENTIONS_PER_TEXT = 20;
/**
 * Strip the marker's delimiters from a display name.
 *
 * This is what stops a user from forging a grant by *being called* something
 * like `Bob](user:VICTIM_ID)(x` — without this, their name would close the
 * label group early and open a second, attacker-chosen id group.
 *
 * Defence in depth only: the server ignores the label entirely and reads just
 * the id, so even an unsanitized label cannot grant anything.
 */
export const sanitizeMentionLabel = (displayName) => displayName.replace(/[[\]()\r\n]/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 100);
/** Build the stored marker for a user. Used by the mention picker on insert. */
export const formatMention = (userId, displayName) => `@[${sanitizeMentionLabel(displayName)}](user:${userId})`;
/**
 * The user ids mentioned in `text`, de-duplicated, in first-appearance order.
 *
 * This is the ONLY function the server should use to decide who was mentioned.
 * Never accept a client-supplied array of mentioned ids: the text is what gets
 * rendered, so deriving the grant from the text is what guarantees that "who
 * has access" always equals "who is shown on screen".
 */
export const parseMentionIds = (text) => {
    const mentionedUserIds = [];
    for (const mentionMatch of text.matchAll(MENTION_PATTERN)) {
        const mentionedUserId = mentionMatch[2];
        if (!mentionedUserIds.includes(mentionedUserId)) {
            mentionedUserIds.push(mentionedUserId);
        }
    }
    return mentionedUserIds;
};
/**
 * Split `text` into plain-text and mention segments.
 *
 * Renderers MUST consume this and emit React nodes. Do not reconstruct HTML
 * from it and hand that to `dangerouslySetInnerHTML` — the label is
 * user-controlled text.
 */
export const splitMentions = (text) => {
    const segments = [];
    let plainTextStartIndex = 0;
    for (const mentionMatch of text.matchAll(MENTION_PATTERN)) {
        const mentionStartIndex = mentionMatch.index ?? 0;
        if (mentionStartIndex > plainTextStartIndex) {
            segments.push({ kind: 'text', value: text.slice(plainTextStartIndex, mentionStartIndex) });
        }
        segments.push({ kind: 'mention', label: mentionMatch[1], userId: mentionMatch[2] });
        plainTextStartIndex = mentionStartIndex + mentionMatch[0].length;
    }
    if (plainTextStartIndex < text.length) {
        segments.push({ kind: 'text', value: text.slice(plainTextStartIndex) });
    }
    return segments;
};
/**
 * Render markers as plain `@Name` for contexts that cannot show a chip — email
 * bodies, notification excerpts, search indexes, and the text a user is typing.
 */
export const stripMentions = (text) => text.replace(MENTION_PATTERN, (_fullMatch, mentionLabel) => `@${mentionLabel}`);
/**
 * ## Composing vs. storing
 *
 * The stored format carries the user id (`@[Ann Lee](user:abc-123)`) because the id is the
 * authority for the access grant. But a person typing a comment must never SEE that — an
 * editor that shows `@[Acme Manager](user:1770226980829-96zpfu623)` mid-sentence is
 * unusable, and leaks an internal identifier into the UI.
 *
 * So the composer holds DISPLAY text (`@Ann Lee`) plus the ids out of band, and converts to
 * marker text only on submit. The server still re-parses the submitted markers to derive
 * grants, so the security model is unchanged: the id remains the authority, and the text is
 * still the single source of truth for who was mentioned.
 *
 * The helpers below are that conversion, in both directions.
 */
/** Marker text → the users it mentions. Used to seed the composer when editing. */
export const extractMentions = (markerText) => splitMentions(markerText)
    .filter((segment) => segment.kind === 'mention')
    .map(({ userId, label }) => ({ userId, label }));
/**
 * Display text + tracked ids → the marker text that gets stored.
 *
 * Fails SAFE by construction: a mention whose `@Label` the user has edited or deleted is no
 * longer found in the text, so it is simply not re-marked — it becomes plain prose and
 * grants nobody access. The alternative (keeping a grant whose text no longer shows the
 * name) is exactly the invisible-grant bug the marker format exists to prevent.
 *
 * Longest labels are matched first, so mentioning both "Ann" and "Ann Lee" does not let the
 * shorter name consume the longer one's text.
 */
export const toMarkerText = (displayText, mentionedUsers) => {
    const byLongestLabel = [...mentionedUsers].sort((left, right) => right.label.length - left.label.length);
    let markerText = displayText;
    for (const mentionedUser of byLongestLabel) {
        const displayToken = `@${mentionedUser.label}`;
        const tokenIndex = markerText.indexOf(displayToken);
        if (tokenIndex === -1)
            continue; // edited away — no grant.
        markerText =
            markerText.slice(0, tokenIndex) +
                formatMention(mentionedUser.userId, mentionedUser.label) +
                markerText.slice(tokenIndex + displayToken.length);
    }
    return markerText;
};
